// core/utils/exportToExcel.js
import * as XLSX from "xlsx-js-style";

/**
 * Exporta un array de objetos a un archivo Excel (.xlsx) con estilos.
 *
 * @param {Array} data - Array de objetos con los datos (ya formateados)
 * @param {Array} columns - Array de { key, label, style? } para orden y encabezados
 * @param {String} fileName - Nombre del archivo sin extensión
 * @param {String} sheetName - Nombre de la hoja
 * @param {Object} headerStyle - Estilo del encabezado
 * @param {Function} getRowStyle - Callback (row, index) => style para cada fila
 */
export const exportToExcel = ({
  data = [],
  columns = [],
  fileName = "reporte",
  sheetName = "Reporte",
  headerStyle,
  getRowStyle,
  getCellStyle,
  getHeaderCellStyle,
}) => {
  if (!data || data.length === 0) {
    console.warn("No hay datos para exportar");
    return;
  }

  // ============================================================
  // 1. Construir matriz fila por fila (array de arrays)
  // ============================================================
  const headerRow = columns.map((col) => col.label);
  const dataRows = data.map((item) => columns.map((col) => item[col.key] ?? ""));

  const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);

  // ============================================================
  // 2. Ancho de columnas automático
  // ============================================================
  const colWidths = columns.map((col, colIdx) => {
    const maxLength = Math.max(
      String(col.label).length,
      ...dataRows.map((r) => String(r[colIdx] || "").length)
    );
    return { wch: Math.min(maxLength + 2, 40) };
  });
  worksheet["!cols"] = colWidths;

  // ============================================================
  // 3. Aplicar estilos
  // ============================================================
  const range = XLSX.utils.decode_range(worksheet["!ref"]);

  // 3.1 Estilo por defecto del header
  const defaultHeaderStyle = {
    font: { bold: true, color: { rgb: "1B5E20" }, sz: 11 },
    fill: { fgColor: { rgb: "DCEDC8" } }, // verde claro
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "689F38" } },
      bottom: { style: "thin", color: { rgb: "689F38" } },
      left: { style: "thin", color: { rgb: "689F38" } },
      right: { style: "thin", color: { rgb: "689F38" } },
    },
  };

  // Header
  for (let C = range.s.c; C <= range.e.c; C++) {
    const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: C })];
    if (!cell) continue;
    const base = headerStyle || defaultHeaderStyle;
    const custom = getHeaderCellStyle?.(columns[C], C) || {};
    cell.s = {
      ...base,
      ...custom,
      font: { ...base.font, ...custom.font },
      alignment: { ...base.alignment, ...custom.alignment },
      border: { ...base.border, ...custom.border },
    };
  }

  // Filas
  for (let R = 1; R <= range.e.r; R++) {
    const rowData = data[R - 1];
    const rowStyle = getRowStyle ? getRowStyle(rowData, R - 1) : null;

    for (let C = range.s.c; C <= range.e.c; C++) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = worksheet[cellAddress];
      if (!cell) continue;

      // Estilo base
      cell.s = {
        font: { sz: 10, color: { rgb: "1F2937" } },
        alignment: { horizontal: "left", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "E5E7EB" } },
          bottom: { style: "thin", color: { rgb: "E5E7EB" } },
          left: { style: "thin", color: { rgb: "E5E7EB" } },
          right: { style: "thin", color: { rgb: "E5E7EB" } },
        },
      };

      // Combinar los estilos de fila y de celda.
      const cellStyle = getCellStyle?.(rowData, columns[C], C) || {};
      for (const style of [rowStyle, cellStyle]) {
        if (!style) continue;
        cell.s = {
          ...cell.s,
          ...style,
          font: { ...cell.s.font, ...style.font },
          alignment: { ...cell.s.alignment, ...style.alignment },
          border: { ...cell.s.border, ...style.border },
        };
      }
    }
  }

  // ============================================================
  // 4. Descargar archivo
  // ============================================================
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  XLSX.writeFile(workbook, `${fileName}.xlsx`);
  return workbook;
};
